import React, { useCallback } from "react";
import * as Scrivito from "scrivito";
import { UncontrolledTooltip } from "reactstrap";
import { formatDate } from "utils";
import { Checkbox, Dropdown, FilterTag } from "components";
import NiceModal from "@ebay/nice-modal-react";
import { ProjectOrFolderDownloadModal } from "components/_Modals/ProjectDownloadModal/ProjectOrFolderDownloadModal";
import { ShareProjectModal } from "components/_Modals/";
import { useMutation } from "urql";
import { DuplicateProjectMut } from "api/mutations";
import { Spinner } from "components/Spinner";
import cx from "classnames";
import { useNavigateToProject } from "hooks/projectHooks";
import { PROJECT_LIST_FETCH_DELAY } from "variables";
import { useUser } from "stores/user";
import { useProjectListContext } from "../../hooks/useProjectListContext";

import s from "./ProjectListTableRow.module.scss";
import { useLanguage } from "stores/globalStore";

export const ProjectListTableRow = Scrivito.connect(
  ({
    toggleProjectMenu,
    toggleModalStatus,
    toggleDeleteModal,
    handleCheckboxClick,
    isChecked,
    project,
    translations,
    spinnerId,
  }) => {
    const {
      statusColors,
      refetchProjectList,
      setCurrentPageNo,
      resetUrlParams,
    } = useProjectListContext();

    const navigateToProject = useNavigateToProject();

    const {
      id: projectId,
      title,
      updatedAt,
      lastEditBy,
      sharedWith,
      status,
    } = project;

    const loadingState = spinnerId === project.id;
    const lang = useLanguage();
    const user = useUser();

    const [replication, exeDuplicateMutation] =
      useMutation(DuplicateProjectMut);

    const handleSomethingClicked = useCallback(() => {}, []);

    const handleDuplicate = useCallback(() => {
      exeDuplicateMutation({
        input: {
          id: projectId,
          languageCode: lang,
          recipientEmail: user ? user.email : "",
        },
      }).then(() => {
        // Delay is required to allow the DS cache to refresh before updating projectList. See task 58816.
        setTimeout(() => {
          refetchProjectList();
          resetUrlParams();
          setCurrentPageNo(1);
        }, PROJECT_LIST_FETCH_DELAY);
      });
    }, [
      exeDuplicateMutation,
      projectId,
      lang,
      user,
      refetchProjectList,
      setCurrentPageNo,
      resetUrlParams,
    ]);

    const handleDownloadPopupClick = () => {
      NiceModal.show(ProjectOrFolderDownloadModal, {
        modalSubtitle: title,
        projectId,
      });
    };

    const handleNavigateToProjectItem = useCallback(() => {
      if (!loadingState) {
        navigateToProject.item(projectId);
      }
    }, [loadingState, navigateToProject, projectId]);

    const handleProjectSelect = useCallback(() => {
      handleCheckboxClick(projectId);
    }, [handleCheckboxClick, projectId]);

    const handleMenuOpen = useCallback(() => {
      toggleProjectMenu(project);
    }, [project, toggleProjectMenu]);

    const toggleShareModal = useCallback(() => {
      NiceModal.show(ShareProjectModal, { project, refetchProjectList }).then();
    }, [project, refetchProjectList]);
    return (
      <>
        <td
          style={{ width: "25%" }}
          data-testid={`PROJECT_LIST_PROJECT-${projectId}`}
        >
          <div className="d-flex flex-row align-items-center h-100">
            {/* TODO: define font-sizes related to rem and replace font16 etc. */}
            <Checkbox
              checked={isChecked}
              onChange={handleProjectSelect}
              wrapperClassName="me-3"
              disabled={loadingState}
            />
            <Scrivito.LinkTag
              data-testid="ProjectListTableRow_project-item-link"
              className="w-100"
              onClick={handleNavigateToProjectItem}
            >
              <div className="d-flex text-gray-700 fw-bold">
                <span
                  id={`projectId${projectId}`}
                  className={` ${s.textWrapEllipsis}`}
                >
                  {title}
                </span>
              </div>
            </Scrivito.LinkTag>
            {title && title.length > 25 && (
              <UncontrolledTooltip
                target={`projectId${projectId}`}
                placement="top"
              >
                {title}
              </UncontrolledTooltip>
            )}
          </div>
        </td>
        <td
          style={{ width: "20%" }}
          data-testid={`PROJECT_LIST_LAST_MODIFICATION-${projectId}`}
        >
          <div className={s.rowItem}>
            {formatDate(updatedAt, "DD.MM.YYYY", "YYYY-MM-DD")}
          </div>
          <div className={s.rowItem}>
            {translations.PROJECT_EDITED_BY(lastEditBy.fullName)}
          </div>
        </td>
        <td
          className="d-flex align-items-center justify-content-between"
          style={{ width: "20%" }}
          data-testid={`PROJECT_SHARING_SETTINGS-${projectId}`}
        >
          <FilterTag
            id={3}
            name={
              sharedWith?.length > 0
                ? translations.SHARED
                : translations.NOT_SHARED
            }
            icon={
              sharedWith?.length > 0 ? "user-group-regular" : "user-regular"
            }
            onClick={handleSomethingClicked}
          />
        </td>
        <td
          className="d-flex align-items-center justify-content-between"
          style={{ width: "35%" }}
          data-testid={`PROJECT_LIST_STATUS-${projectId}`}
        >
          <div>
            <FilterTag
              id={4}
              name={status.status}
              color={statusColors[status.status]}
              onClick={handleSomethingClicked}
            />
          </div>
          <div className="position-relative d-flex align-items-center w-fit-content">
            <Scrivito.LinkTag
              data-testid={`DETAILS-${projectId}`}
              className={cx("me-3 btn btn-outlined", {
                "btn-outlined-off": loadingState,
              })}
              onClick={handleNavigateToProjectItem}
              role="button"
            >
              {translations.DETAILS}
            </Scrivito.LinkTag>
            {loadingState || replication.fetching ? (
              <Spinner className="loader" size="xs" color="primary" />
            ) : (
              <Dropdown
                openToTopForce={true}
                btnClassName="btn-option"
                testId={`DREI_PUNKT_MNU-${projectId}`}
                disabled={isChecked}
                options={[
                  {
                    id: "project_status_update",
                    title: translations.PROJECT_STATUS_UPDATE,
                    onClick: toggleModalStatus,
                    testId: `PROJECT_STATUS_UPDATE-${projectId}`,
                  },
                  {
                    id: "download",
                    title: translations.DOWNLOAD,
                    onClick: handleDownloadPopupClick,
                    testId: `PROJECT_DOWNLOAD-${projectId}`,
                  },
                  {
                    id: 2,
                    title: translations.DUPLICATE,
                    onClick: handleDuplicate,
                    testId: `PROJECT_DUPLICATE-${projectId}`,
                  },
                  /* TODO: forthcoming feature (hidden acc US-19953) */
                  {
                    id: 3,
                    title: translations.SHARE,
                    onClick: toggleShareModal,
                    testId: `SHARE-${projectId}`,
                  },
                  {
                    id: 4,
                    title: translations.DELETE,
                    onClick: toggleDeleteModal,
                    testId: `DELETE-${projectId}`,
                  },
                ]}
                onClick={handleMenuOpen}
              />
            )}
          </div>
        </td>
      </>
    );
  },
);
